// @ts-nocheck
// Swipe deck order, shared by the homepage feed (assets/vhp-home-feed.js + vhp-collection-reels.js) and the
// planner's setup deck (assets/vhp-planner-core.js deckOrder). Pure and seeded; only browserSeed() and
// pageStats() touch the browser.
//
// Score per recipe: popularity (position-adjusted like rate vs the average, from the recipe_stats shop metafield
// written daily by vhp-planner /api/cron/recipe-stats; neutral without stats) + freshness (first 30 days) + seeded
// jitter. The jitter is per recipe, not per list, so the homepage and the planner rank the same recipes the same
// way for one browser. Then a spacing pass: no two recipes with the same main ingredient back to back (looking a
// few cards ahead), at least 6 mains in the first 10 of a mixed list, and the newest recipe of the last 30 days no
// later than the 4th card. 20 % of seeds get a plain shuffle instead: an unbiased slice for the stats and the
// baseline the ranking is compared against.
// Taste (opts.taste, docs/FEED_ORDER.md step 4): once a visitor has 5 likes + skips, main ingredients and tags of
// their likes move up and the ones they keep skipping move down; a known goal leans toward protein per kcal (lose)
// or protein (gain). Every 4th card still comes from the plain ranking (~25 % unpersonalised); the shuffle group
// ignores taste. Homepage reel (browserTaste): a save counts twice, a card she watched long, opened or shared counts
// as a soft like, a card she scrolled past in a moment as half a skip.
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.vhpDeckOrder = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const SHUFFLE_SHARE = 0.2;
  const FRESH_DAYS = 30;
  const LOOKAHEAD = 4; // cards looked ahead for one with another main ingredient
  const FRONT = 10;
  const FRONT_MAINS = 6;
  const NEWEST_AT = 3; // index: the 4th card
  const EX_BOOST = 0.15;
  const STORAGE_KEY = 'vhp_planner_anon'; // assets/vhp-planner-likes.js; the planner keeps its seedKey there too
  const SEED_RE = /^[a-z0-9]{8,40}$/; // same as assets/vhp-planner-funnel.js
  const TASTE_MIN = 5; // likes + skips before the order tilts toward them
  const TASTE_KEEP = 4; // every 4th card from the plain ranking
  const TASTE_RECENT = 60; // most recent likes and skips each
  const TASTE_PRIOR = 3; // pseudo-swipes at the visitor's own like share per feature
  const TASTE_MAX = 0.4;
  const GOAL_WEIGHT = 0.25;
  const FAV_SLOT_MIN = 3; // likes before favouriteSlot names one
  // Taste weights of the reel's signals (browserTaste); the planner passes likes and skips only (weight 1 each).
  const REEL_LIKE_WEIGHT = 2;
  const SOFT_LIKE_WEIGHT = 1;
  const PASS_WEIGHT = 0.5;

  // Same hash and generator as assets/vhp-planner-core.js, so a seed means the same thing in both.
  const seed = (...parts) => {
    const text = parts.map((part) => String(part ?? '')).join('|');
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  };

  const rng = (s) => {
    let a = s >>> 0;
    return () => {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const productId = (id) => {
    const match = String(id ?? '').match(/(c?\d+)$/);
    return match ? match[1] : '';
  };

  // Main ingredient from tags and title (ids as in vhp-planner-hub-rules.js mainIngredient, which reads the zutaten;
  // the planner passes that one as opts.kind and this is the fallback). Null when nothing matches.
  const KINDS = [
    ['ing-tofu', /tofu/],
    ['ing-tempeh', /tempeh/],
    ['ing-hack', /\bhack|sojagranulat|sojaschnetzel/],
    ['ing-seitan', /seitan/],
    ['ing-legumes', /linse|kichererbse|bohne|edamame|chickpea/],
    ['ing-pasta', /pasta|nudel|spaghetti|penne|fusilli|makkaroni|maccheroni|lasagne|tagliatelle|rigatoni|gnocchi|udon/],
    ['ing-potato', /kartoffel|potato|pommes|fries/],
  ];
  const kindCache = new Map();
  const textKind = (card) => {
    const id = productId(card?.id);
    if (id && kindCache.has(id)) return kindCache.get(id);
    const tags = Array.isArray(card?.tags) ? card.tags.join('|') : String(card?.tags || '');
    const text = `${tags}|${card?.title || ''}`.toLowerCase();
    const kind = KINDS.find(([, re]) => re.test(text))?.[0] || null;
    if (id) kindCache.set(id, kind);
    return kind;
  };

  const ageDays = (card, now) => {
    const pub = Date.parse(card?.pub || '');
    return now && Number.isFinite(pub) ? (now - pub) / 86400000 : Infinity;
  };
  const isFresh = (card, now) => {
    const age = ageDays(card, now);
    return age >= 0 && age < FRESH_DAYS;
  };

  // Mains up front, main ingredients alternating, the newest recipe early. `ordered` is best first.
  const spread = (ordered, now, kindOf) => {
    const rest = ordered.slice();
    const out = [];
    const isMain = (card) => card.slot === 'main';
    const mixed = rest.some(isMain) && rest.some((card) => !isMain(card));
    let newest = null;
    rest.forEach((card) => {
      if (isFresh(card, now) && (!newest || ageDays(card, now) < ageDays(newest, now))) newest = card;
    });
    let mains = 0;
    while (rest.length) {
      const at = out.length;
      let pick = -1;
      if (newest && at === NEWEST_AT) pick = rest.indexOf(newest);
      if (pick < 0) {
        const needMain = mixed && at < FRONT && at - mains >= FRONT - FRONT_MAINS;
        const prev = at ? kindOf(out[at - 1]) : null;
        for (let j = 0; j < Math.min(rest.length, LOOKAHEAD); j += 1) {
          if (needMain && !isMain(rest[j])) continue;
          if (prev && kindOf(rest[j]) === prev) continue;
          pick = j;
          break;
        }
        if (pick < 0 && needMain) pick = rest.findIndex(isMain);
        if (pick < 0) pick = 0;
      }
      const [card] = rest.splice(pick, 1);
      if (card === newest) newest = null;
      if (isMain(card)) mains += 1;
      out.push(card);
    }
    return out;
  };

  const tagList = (card) =>
    (Array.isArray(card?.tags) ? card.tags : String(card?.tags || '').split(/[|,]/))
      .map((tag) => String(tag).trim().toLowerCase())
      .filter(Boolean);
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  // Likes vs skips per feature (main ingredient, tag), against the visitor's own like share and smoothed, so a tag
  // on every recipe or a single swipe moves nothing. taste.cards: where liked / skipped ids are looked up besides the
  // ranked list (the planner ranks one slot, the likes span all). Weighted: taste.likeWeight per like (default 1),
  // taste.soft (soft likes) and taste.passes (soft skips) at their own weight. Null below TASTE_MIN known signals.
  const tasteScore = (taste, list, kindOf) => {
    const byId = new Map();
    [...(taste?.cards || []), ...list].forEach((card) => card && byId.set(productId(card.id), card));
    const features = (card) => {
      const kind = kindOf(card);
      return [...(kind ? [`k:${kind}`] : []), ...new Set(tagList(card).map((tag) => `t:${tag}`))];
    };
    const counts = new Map();
    const total = [0, 0];
    let known = 0;
    const count = (ids, at, weight) =>
      (ids || []).slice(-TASTE_RECENT).forEach((id) => {
        const card = byId.get(productId(id));
        if (!card) return;
        known += 1;
        total[at] += weight;
        features(card).forEach((f) => {
          const row = counts.get(f) || [0, 0];
          row[at] += weight;
          counts.set(f, row);
        });
      });
    count(taste?.likes, 0, Number(taste?.likeWeight) || 1);
    count(taste?.soft, 0, SOFT_LIKE_WEIGHT);
    count(taste?.skips, 1, 1);
    count(taste?.passes, 1, PASS_WEIGHT);
    if (known < TASTE_MIN) return null;
    const share = clamp(total[0] / (total[0] + total[1]), 0.15, 0.85);
    const aff = (f) => {
      const row = counts.get(f);
      if (!row || row[0] + row[1] < 2) return null;
      return (row[0] + TASTE_PRIOR * share) / (row[0] + row[1] + TASTE_PRIOR) - share;
    };
    return (card) => {
      const kind = kindOf(card);
      const k = kind ? aff(`k:${kind}`) || 0 : 0;
      const tags = tagList(card).map((tag) => aff(`t:${tag}`)).filter((a) => a != null);
      const t = tags.length ? tags.reduce((sum, a) => sum + a, 0) / tags.length : 0;
      return clamp(k + t, -TASTE_MAX, TASTE_MAX);
    };
  };

  // Goal lean against the slot's median: protein per kcal for lose, protein for gain; nothing for maintain.
  const goalScore = (goal, list) => {
    if (goal !== 'lose' && goal !== 'gain') return null;
    const metric = (card) => {
      const kcal = Number(card.kcal) || 0;
      const protein = Number(card.protein) || 0;
      if (!protein || !kcal) return 0;
      return goal === 'lose' ? protein / kcal : protein;
    };
    const bySlot = {};
    list.forEach((card) => {
      const m = metric(card);
      if (m) (bySlot[card.slot] = bySlot[card.slot] || []).push(m);
    });
    const median = {};
    Object.entries(bySlot).forEach(([slot, values]) => {
      values.sort((a, b) => a - b);
      median[slot] = values[Math.floor(values.length / 2)];
    });
    if (!Object.keys(median).length) return null;
    return (card) => {
      const m = metric(card);
      const mid = median[card.slot];
      return m && mid ? GOAL_WEIGHT * clamp(m / mid - 1, -1, 1) : 0;
    };
  };

  // opts: { stats, seed, now, kind(card), taste: { likes: [id], skips: [id], goal, cards } }. Returns { cards,
  // shuffled, personal }; the input array is not changed.
  const rank = (cards, opts) => {
    const base = opts?.seed ?? 0;
    const shuffled = rng(base)() < SHUFFLE_SHARE;
    const jitter = (card) => rng(seed(base, productId(card.id)))();
    const list = (cards || []).filter(Boolean);
    if (shuffled) {
      const keyed = list.map((card) => ({ card, key: jitter(card) }));
      keyed.sort((a, b) => b.key - a.key);
      return { cards: keyed.map((row) => row.card), shuffled, personal: false };
    }
    const now = Number(opts?.now) || 0;
    const stats = opts?.stats;
    const avg = Number(stats?.avg?.like) || 0;
    const keyed = list.map((card) => {
      const row = stats?.r?.[productId(card.id)];
      const pop = row ? 4 * ((Number(row[6]) || 0) - avg) : 0;
      const age = ageDays(card, now);
      const fresh = age >= 0 && age < FRESH_DAYS ? 0.3 * (1 - age / FRESH_DAYS) : 0;
      // Exclusives in a subscriber's feed (card.ex): what they pay for, a little further up.
      return { card, key: pop + fresh + (card.ex ? EX_BOOST : 0) + jitter(card) * 0.6 };
    });
    const byKey = (a, b) => b.key - a.key || productId(a.card.id).localeCompare(productId(b.card.id));
    keyed.sort(byKey);
    const kindOf = (card) => (opts?.kind && opts.kind(card)) || textKind(card);
    const like = tasteScore(opts?.taste, list, kindOf);
    const lean = goalScore(opts?.taste?.goal, list);
    if (!like && !lean) return { cards: spread(keyed.map((row) => row.card), now, kindOf), shuffled, personal: false };
    // Personal order, with every TASTE_KEEP-th card taken from the plain one.
    const personal = keyed
      .map((row) => ({ card: row.card, key: row.key + (like ? like(row.card) : 0) + (lean ? lean(row.card) : 0) }))
      .sort(byKey);
    const used = new Set();
    const out = [];
    let p = 0;
    let q = 0;
    while (out.length < keyed.length) {
      const fromPlain = out.length % TASTE_KEEP === TASTE_KEEP - 1;
      const src = fromPlain ? keyed : personal;
      let i = fromPlain ? q : p;
      while (used.has(src[i].card)) i += 1;
      used.add(src[i].card);
      out.push(src[i].card);
      if (fromPlain) q = i + 1;
      else p = i + 1;
    }
    return { cards: spread(out, now, kindOf), shuffled, personal: true };
  };

  // The slot with the most likes (ties: SLOT_ORDER of vhp-planner-likes.js), null below FAV_SLOT_MIN likes.
  const favouriteSlot = (likes) => {
    const counts = {};
    (likes || []).forEach((item) => {
      if (item?.slot) counts[item.slot] = (counts[item.slot] || 0) + 1;
    });
    if ((likes || []).length < FAV_SLOT_MIN) return null;
    return ['main', 'breakfast', 'snack', 'dessert'].reduce((best, slot) => ((counts[slot] || 0) > (counts[best] || 0) ? slot : best), 'main');
  };

  // Likes, skips and goal of this browser (planner document, assets/vhp-planner-likes.js) as opts.taste.
  const browserTaste = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') || {};
      const ids = (doc) => (Array.isArray(doc?.items) ? doc.items.map((item) => String(item?.id ?? '')) : []);
      const likes = ids(raw.likes);
      const skips = ids(raw.skips);
      // Reel layout (theme docs/FEED_REEL.md, docs/FEED_ORDER.md "Reel signals"): cards she scrolled past within a
      // moment are soft skips, cards she watched long, opened or shared soft likes, for the taste only
      // (vhp-collection-reels.js PASS_KEY / ENGAGED_KEY, id → ms); they never enter the planner document. Oldest
      // first, so TASTE_RECENT keeps the latest.
      const byTime = (key) => {
        try {
          const map = JSON.parse(localStorage.getItem(key) || 'null') || {};
          return Object.keys(map).sort((a, b) => Number(map[a]) - Number(map[b]));
        } catch (_) {
          return [];
        }
      };
      const known = new Set([...likes, ...skips]);
      const soft = byTime('vhp_feed_engaged_v1').filter((id) => !known.has(id));
      const engaged = new Set(soft);
      const passes = byTime('vhp_feed_pass_v1').filter((id) => !known.has(id) && !engaged.has(id));
      return { likes, skips, soft, passes, likeWeight: REEL_LIKE_WEIGHT, goal: typeof raw.goal === 'string' ? raw.goal : null };
    } catch (_) {
      return null;
    }
  };

  // The planner's per-browser seedKey (same localStorage doc), created here when the homepage comes first.
  const browserSeed = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') || {};
      if (typeof raw.seedKey === 'string' && SEED_RE.test(raw.seedKey)) return raw.seedKey;
      raw.seedKey = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(36).padStart(2, '0')).join('');
      localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
      return raw.seedKey;
    } catch (_) {
      return 'anon';
    }
  };

  // recipe_stats embedded by sections/vhp-home-feed.liquid (#vhpRecipeStats); null without it.
  let statsCache;
  const pageStats = () => {
    if (statsCache !== undefined) return statsCache;
    try {
      statsCache = JSON.parse(document.getElementById('vhpRecipeStats')?.textContent || 'null');
    } catch (_) {
      statsCache = null;
    }
    return statsCache;
  };

  return { SHUFFLE_SHARE, FRESH_DAYS, TASTE_MIN, seed, rng, textKind, rank, favouriteSlot, browserSeed, browserTaste, pageStats };
});
